import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { RecommendationsList } from '../../components/RecommendationsList';
import { selectRecommendations } from '../../selectors/recommendations';
import { RecommendationOfferWithHover } from '../../components/RecommendationOffer';
import { Responsive, useDeviceType } from '@cian/ui-kit';
import { EDeviceType } from '../../types/deviceType';
import { GeoInformationContainer } from '../GeoInformationContainer';
import { ContactButtonsContainer } from '../ContactButtonsContainer';
import { trackEntityTeaserShow, trackRecommendationClick } from './tracking';
import { useVisitedOffersContext } from '@cian/frontend-visited-offers-widget';
import { LabelsContainer } from '../LabelsContainer';
import { trackPageLoad } from '../../utils/analytics';
import { selectUserId } from '../../selectors/user';
import { IApplicationState } from '../../types/redux';
import { TRecommendation } from '../../types/recommendation';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';

export const RecommendationsContainer = () => {
  const recommendations = useSelector<IApplicationState, TRecommendation[]>(selectRecommendations, shallowEqual);
  const device = useDeviceType();
  const userId = useSelector(selectUserId);
  const isMobile = device === EDeviceType.Phone;
  const { setOfferVisited, isOfferVisited, isLoaded } = useVisitedOffersContext();

  const handleRecommendationClick = React.useCallback(
    (offer: TRecommendation, position: number) => {
      setOfferVisited(offer.offerId);
      trackRecommendationClick({ offer, position, deviceType: device });
    },
    [setOfferVisited, device],
  );

  React.useEffect(() => {
    if (isLoaded) {
      trackPageLoad({
        deviceType: device,
        userId,
        products: recommendations.map(({ offerId }: TRecommendation, index: number) => ({
          id: offerId,
          offerType: 'offer',
          position: index + 1,
          extra: isOfferVisited(offerId) ? { is_viewes: true } : undefined,
        })),
      });
    }
  }, [device, isLoaded, recommendations, userId, isOfferVisited]);

  const handleEntityTrackTeaserShow = React.useCallback((offerId: number, position: number, isMobile: boolean) => {
    trackEntityTeaserShow(offerId, position, isMobile);
  }, []);

  const recommendationsList = React.useMemo(
    () =>
      recommendations.map((recOffer: TRecommendation, position: number) => (
        <div key={recOffer.offerId}>
          <ActionAfterViewObserver
            percentVisible={0.5}
            callback={() => handleEntityTrackTeaserShow(recOffer.offerId, position + 1, isMobile)}
            triggerOnce
            callbackDelay={1000}
          >
            <RecommendationOfferWithHover
              position={position + 1}
              isMobile={isMobile}
              geoInformation={<GeoInformationContainer category={recOffer.category} geo={recOffer.geo} />}
              offer={recOffer}
              onClick={handleRecommendationClick}
              labels={<LabelsContainer visited={isOfferVisited(recOffer.offerId)} />}
            />
          </ActionAfterViewObserver>
          <Responsive phone>
            <ContactButtonsContainer offerId={recOffer.offerId} position={position} />
          </Responsive>
        </div>
      )),
    [recommendations, isMobile, handleRecommendationClick, isOfferVisited, handleEntityTrackTeaserShow],
  );

  return <RecommendationsList>{recommendationsList}</RecommendationsList>;
};
